import {
  Alert,
  Divider,
  Flex,
  Loader,
  ScrollArea,
  Text,
  Title,
} from "@mantine/core";
import React, { useCallback } from "react";
import useInsightsDrilldown from "hooks/useInsightsDrilldown";
import { formatNumber, hoverStateOfInsightNumbers } from "utils/common";
import { generateMeetingCostInsights } from "utils/insights";
import EmptyCard from "../EmptyCard/EmptyCard";
import { TbUserDollar } from "react-icons/tb";

const CostCard = ({
  type,
  isLoading,
  isError,
  data,
  label = "Cost of meetings",
  source = "myTime",
  duration,
  self,
  uid,
}) => {
  const { openDrilldown } = useInsightsDrilldown();

  const insightData = generateMeetingCostInsights(data, type);

  // `self` is one perspective on the organizer and attendee tabs, and both of
  // them on the combined view — the nightly job writes no "all" key for cost.
  const buildRedisKeys = useCallback(
    (bucketKey) => {
      if (!uid || !duration || !self || !bucketKey) return [];
      const collection =
        source === "individual" ? "userCalculations" : "orgCalculations";
      const perspectives = Array.isArray(self) ? self : [self];
      return perspectives.map(
        (perspective) =>
          `users:${uid}:${collection}:${duration}:${perspective}:${bucketKey}`
      );
    },
    [uid, duration, self, source]
  );

  if (isLoading) {
    return (
      <CostCardWrapper type={type} label={label} data={data}>
        <div className="loader">
          <Loader />
          <span>Fetching cost details</span>
        </div>
      </CostCardWrapper>
    );
  }

  if (isError) {
    return (
      <CostCardWrapper type={type} label={label} data={data}>
        <div className="loader">
          <span>{isError}</span>
        </div>
      </CostCardWrapper>
    );
  }
  if (!data) {
    return (
      <CostCardWrapper label={label}>
        <div className="loader">
          <EmptyCard />
        </div>
      </CostCardWrapper>
    );
  }
  const handleNoOfMeetingsClick = (selection) => {
    openDrilldown({
      name: selection.name,
      type: selection.type,
      meetingType: selection.meetingType,
      redisKeys: buildRedisKeys(selection.key),
    });
  };
  return (
    <CostCardWrapper type={type} label={label} data={data}>
      <Flex align={"flex-end"} gap={"3.5rem"} justify={"space-between"}>
        <Title
          order={2}
          onClick={() =>
            handleNoOfMeetingsClick({
              name: "Cost of meetings",
              type: "Meetings",
              meetingType: "All",
              meetingIds: data?.totalMeetingsCostIds,
              key: "totalMeetingsCostIds",
            })
          }
          sx={{
            ...hoverStateOfInsightNumbers,
          }}
        >
          ${formatNumber(data?.totalMeetingsCost)}
        </Title>
      </Flex>
      <div style={{ width: "100%" }}>
        <div style={{ fontWeight: "bold", marginBottom: ".4rem" }}>
          <Flex
            justify={"space-between"}
            sx={hoverStateOfInsightNumbers}
            onClick={() => {
              handleNoOfMeetingsClick({
                name: "Cost of meetings",
                type: "Meetings",
                meetingType: "External",
                meetingIds: data?.externalMeetingsCostIds,
                key: "externalMeetingsCostIds",
              });
            }}
          >
            <Text color={"rgba(0, 0, 0, 0.25)"}>External Meetings</Text>
            <Text>${formatNumber(data?.externalMeetingsCost)}</Text>
          </Flex>
        </div>

        <div style={{ fontWeight: "bold" }}>
          <Flex
            justify={"space-between"}
            sx={hoverStateOfInsightNumbers}
            onClick={() => {
              handleNoOfMeetingsClick({
                name: "Cost of meetings",
                type: "Meetings",
                meetingType: "Internal",
                meetingIds: data?.internalMeetingsCostIds,
                key: "internalMeetingsCostIds",
              });
            }}
          >
            <Text color={"rgba(0, 0, 0, 0.25)"}>Internal Meetings</Text>
            <Text>${formatNumber(data?.internalMeetingsCost)}</Text>
          </Flex>
        </div>

        {/* message */}
        <Flex direction="column" mt={15}>
          <Divider my={10} />
          <Alert
            color={"blue"}
            variant="light"
            size="sm"
            icon={insightData.icon}
          >
            <ScrollArea h={90}>{insightData.message}</ScrollArea>
          </Alert>
        </Flex>
      </div>
    </CostCardWrapper>
  );
};

export default CostCard;

const CostCardWrapper = ({
  type,
  children,
  data,
  label = "Cost of meetings",
}) => {
  return (
    <div className="card">
      <Flex
        direction={"column"}
        gap={"1rem"}
        sx={{ width: "100%", flexGrow: 1 }}
        justify={"space-between"}
        align={"flex-start"}
      >
        <Flex justify={"space-between"} align={"center"} direction={"column"}>
          <Flex align={"center"}>
            <TbUserDollar size={18} style={{ marginRight: 8 }} />
            <Title order={5}>
              {label}{" "}
              {type === "attendee"
                ? "attended"
                : type === "organizer"
                ? "organized"
                : ""}
            </Title>
          </Flex>
        </Flex>
        {data?.totalMeetingsCost !== null ? children : <EmptyCard />}
      </Flex>
    </div>
  );
};
